import { setDisplayErrorSnackBar } from "../../features/userSlice";
import { setFiltersLoading } from "../../features/monitoringProcessLogsSlice";
import store from "../../store";
import hostname from "./config.host";
import { fetchWrapper } from "./fetch-wrapper";
const apiUrl: string = `${hostname}/aladdin_api/v1`;

function getAllProcessLogs(
  oauthTokenHeader: any,
  from_date: string,
  to_date: string,
  run_id?: string | number,
  filters?: {
    status?: string[];
    run_method?: string[];
    step?: string[];
    process_id?: string[]; // Ensure this matches backend expectation
    package_name?: string[];
    brand_id?: string[];
    interface_id?: string[];
    workflow_name?: string[];
  },
  sort_field?: string,
  sort_order?: "ASC" | "DESC"
) {
  const params = new URLSearchParams();

  // Always include date range
  params.append("from", from_date);
  params.append("to", to_date);

  // Conditionally include run_id
  if (run_id !== undefined && run_id !== null) {
    params.append("run_id", run_id.toString());
  }

  // Preserve active sort when fetching additional batches/pages
  if (sort_field && sort_order) {
    params.append("sort_field", sort_field);
    params.append("sort_order", sort_order);
  }

  // Add optional filters only if they are non-empty
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value.length > 0) {
        // No need for special processing unless required by backend
        const processValue = (val: string | number) => {
          const stringVal = String(val);
          if (key === 'step') {
            return stringVal.charAt(0).toUpperCase() + stringVal.slice(1).toLowerCase();
          }
          return stringVal;
        };

        if (Array.isArray(value)) {
          value.forEach((val) => {
            if (val !== "" && val !== null && val !== undefined) {
              params.append(key, processValue(val));
            }
          });
        } else {
          params.append(key, processValue(value));
        }
      }
    });
  }

  const queryString = params.toString();
  return fetchWrapper.get(
    `${apiUrl}/data_collection/dc_process_log/?${queryString}`,
    oauthTokenHeader
  );
}

async function exportToExcel(
  oauthTokenHeader: string, // Changed from 'any' to 'string' for type safety
  from_date: string,
  to_date: string,
  format: "csv" | "xlsx", // Explicitly define supported formats
  filters?: {
    status?: string[];
    run_method?: string[];
    step?: string[];
    process_id?: string[];
    package_name?: string[];
    brand_id?: string[];
    interface_id?: string[];
    workflow_name?: string[];
  }
): Promise<Blob> {
  // Always return a Blob for consistent handling
  const params = new URLSearchParams();

  // Always include date range and format
  params.append("from", from_date);
  params.append("to", to_date);
  params.append("fmt", format);

  // Add optional filters
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value?.length) {
        // Simplified check
        value.forEach((val) => {
          if (val) {
            const processedVal = (key === 'step') 
              ? String(val).charAt(0).toUpperCase() + String(val).slice(1).toLowerCase()
              : String(val);
            params.append(key, processedVal);
          }
        });
      }
    });
  }

  const url = `${apiUrl}/data_collection/dc_process_log/export/?${params.toString()}`;

  // Use getForCSV for both formats (works for XLSX too)
  const response = await fetchWrapper.getForCSV(url, oauthTokenHeader);
  return response.data; // Return the Blob
}

function postArgoPODLogData(oauthTokenHeader: any, data: any) {
  return fetchWrapper.postURLEncoded(
    `${apiUrl}/operations/argo/podlogviewer/`,
    data,
    oauthTokenHeader
  );
}

function getSingleFilter(filter: string, oauthTokenHeader: any) {
  const params = new URLSearchParams();
  params.append("filters", filter);

  return fetchWrapper.get(
    `${apiUrl}/filters/?${params.toString()}`,
    oauthTokenHeader
  );
}

function getAllFilters(filters: string[], oauthTokenHeader: any) {
  const params = new URLSearchParams();
  filters.forEach((filter) => {
    if (filter) {
      params.append("filters", filter);
    }
  });

  return fetchWrapper.get(
    `${apiUrl}/filters/?${params.toString()}`,
    oauthTokenHeader
  );
}

async function getAllFiltersInParallel(filters: string[], oauthTokenHeader: any) {
  store.dispatch(setFiltersLoading(true));
  try {
    const upperCaseFilters = filters.map((filter) => filter.toUpperCase());
    const filterPromises = upperCaseFilters.map((filter) =>
      getSingleFilter(filter, oauthTokenHeader)
    );
    
    const responses = await Promise.allSettled(filterPromises);
    const combinedData: any = {};
    for (let i = 0; i < responses.length; i++) {
      const result = responses[i];
      const filterName = upperCaseFilters[i];
      if (result.status === 'fulfilled' && result.value?.status === 200) {
        const data = result.value.data?.data?.[filterName] || [];
        combinedData[filterName] = data;
      } else {
        store.dispatch(
          setDisplayErrorSnackBar({
            message: `Failed to fetch filters:`,
            type: "error",
          })
        );
      }
    }
    store.dispatch(setFiltersLoading(false));
    return {
      status: 200,
      data: { data: combinedData },
    };
    
  } catch (error: any) {
    store.dispatch(setFiltersLoading(false));
    store.dispatch(
      setDisplayErrorSnackBar({
        message: `Failed to fetch filters: ${error.message}`,
        type: "error",
      })
    );
    throw error;
  }
}

async function getSortedData(
  oauthTokenHeader: any,
  from_date: string,
  to_date: string,
  sort_field: string,
  sort_order: "ASC" | "DESC",
  run_id?: string | number,
  filters?: {
    status?: string[];
    run_method?: string[];
    step?: string[];
    process_id?: string[];
    package_name?: string[];
    brand_id?: string[];
    interface_id?: string[];
    workflow_name?: string[];
  }
) {
  // Validate sortable fields — backend expects these names directly
  const allowedSortFields = ["run_id", "start_time", "end_time"];
  if (!allowedSortFields.includes(sort_field)) {
    throw new Error(`Sorting not allowed for field: ${sort_field}`);
  }

  const params = new URLSearchParams();

  // Always include date range
  params.append("from", from_date);
  params.append("to", to_date);

  // Add sorting parameters
  params.append("sort_field", sort_field);
  params.append("sort_order", sort_order);

  // Conditionally include run_id
  if (run_id !== undefined && run_id !== null) {
    params.append("run_id", run_id.toString());
  }

  // Add optional filters
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value.length > 0) {
        const processValue = (val: string | number) => {
          const stringVal = String(val);
          if (key === 'step') {
            return stringVal.charAt(0).toUpperCase() + stringVal.slice(1).toLowerCase();
          }
          return stringVal;
        };

        if (Array.isArray(value)) {
          value.forEach((val) => {
            if (val !== "" && val !== null && val !== undefined) {
              params.append(key, processValue(val));
            }
          });
        } else {
          params.append(key, processValue(value));
        }
      }
    });
  }

  const queryString = params.toString();
  return fetchWrapper.get(
    `${apiUrl}/data_collection/dc_process_log/?${queryString}`,
    oauthTokenHeader
  );
}

export const monitoringProcessLogsService = {
  getAllProcessLogs,
  postArgoPODLogData,
  getAllFilters,
  getAllFiltersInParallel,
  exportToExcel,
  getSortedData,
};
